import { api } from './client'
import type { ApiEstimate } from './types'

// MOCK-ONLY API — the task engine. The real backend has no activity, notes,
// calls, tasks or reminders endpoint, and no Customer entity; this whole
// family is new backend work (flagged in the design log). Wire types live
// here rather than in types.ts until the real BE grows a counterpart.
//
// The stage tells her what to do: Pending → review it; Confirmed → call;
// In Progress → the texts run and she calls every two days, the last call
// ends in a wrap-up; Scheduled → did they come in. The system cannot see a
// call happen, so it ASSIGNS a task, she ANSWERS it, and the answer is what
// generates the next one.

export type TaskType = 'review' | 'reach_out' | 'call' | 'callback' | 'fix_contact' | 'close_out'
/** Derived at read time, never persisted — answered through other endpoints. */
export type DerivedTaskType = 'reply' | 'showed'
export type AnyTaskType = TaskType | DerivedTaskType
export type TaskStatus = 'open' | 'done' | 'skipped' | 'cancelled'
export type TaskGroup = 'overdue' | 'now' | 'later' | 'upcoming' | 'done'

export type DeclineReason =
  | 'price'
  | 'another_shop'
  | 'insurance_total_loss'
  | 'not_ready'
  | 'no_longer_needed'
  | 'unreachable'
  | 'other'

export type ReachedThen =
  | 'booked'
  | 'callback'
  | 'sent_estimate'
  | 'needs_changes'
  | 'thinking'
  | 'declined'

export type TaskAnswer =
  | {
      kind: 'reached'
      then: ReachedThen
      callbackAt?: string
      declineReason?: DeclineReason
      declineNote?: string
      appointmentId?: number
      /** Re-enrol from day 0 — the only way the sequence restarts, and only
       *  because she said so. */
      restartDrip?: boolean
    }
  | { kind: 'no_answer' }
  | { kind: 'voicemail' }
  | { kind: 'bad_number' }
  | { kind: 'contact_fixed' }
  | { kind: 'closed'; declineReason?: DeclineReason }
  /** Review only: good to go → Confirmed, and the first call appears. */
  | { kind: 'confirmed' }
  /** Wrap-up only: with a date it's a callback that day; without, one more call. */
  | { kind: 'keep_trying'; callbackAt?: string }
  | { kind: 'parked' }

export type FollowUpOutcome = 'reached' | 'no_answer' | 'voicemail' | 'bad_number'

/** A wait a human decided; the drip shifts by it and carries on. */
export interface DripHold {
  from: string
  to: string | null
  reason: 'callback' | 'parked' | 'snoozed'
}

export type DripState =
  | 'active'
  | 'paused'
  | 'held'
  | 'finished'
  | 'stopped'
  | 'converted'
  | 'not_started'

/** The cached head on estimate.metadata.followUp — rebuilt from tasks. */
export interface EstimateFollowUpHead {
  lastContactAt: string | null
  lastAttemptAt: string | null
  lastOutcome: FollowUpOutcome | null
  lastNote: string | null
  lastUserId: number | null
  lastUserName: string | null
  attempts: number
  /** Consecutive attempts that reached nobody since the last real conversation. */
  unanswered: number
  nextFollowUpAt: string | null
  nextFollowUpSource: 'auto' | 'requested' | 'snoozed' | null
  holds: DripHold[]
  firstOutreachSentAt: string | null
  declineReason: DeclineReason | null
  declinedAt: string | null
  badNumber?: true
}

export interface TaskDoc {
  id: number
  teamId: number
  estimateId: number
  customerId: number | null
  type: TaskType
  attempt: number
  dueAt: string
  createdAt: string
  status: TaskStatus
  skips: number
  answer: TaskAnswer | null
  note: string | null
  answeredAt: string | null
  answeredBy: { id: number; name: string } | null
}

export type AppointmentKind = 'inspection' | 'dropoff'

export interface TaskRow {
  id: number
  type: AnyTaskType
  group: TaskGroup
  estimateId: number
  customerId: number | null
  attempt: number
  /** Call #maxCalls — the last one before the wrap-up. */
  isLastCall: boolean
  dueAt: string
  status: TaskStatus
  skips: number
  answer: TaskAnswer | null
  note: string | null
  answeredAt: string | null
  answeredBy: { id: number; name: string } | null
  customerName: string
  phone: string | null
  email: string | null
  vehicleLabel: string
  estimateStatus: string
  avg: number
  min: number
  max: number
  createdByName: string | null
  head: EstimateFollowUpHead
  /** Copied from the follow-up engine — never recomputed on the client. */
  nextTouchAt: string | null
  nextTouchChannel: 'sms' | 'email' | null
  lastMessage: {
    body: string
    direction: 'inbound' | 'outbound'
    occurredAt: string
    channel: 'sms' | 'email'
  } | null
  /** Where the sequence stands — copied from the engine, never recomputed. */
  dripState: DripState
  dripResumesAt: string | null
  heldBy: 'reply' | 'parked' | null
  /** "2 of 5 sent". */
  textsSent: number
  textsTotal: number
  /** Who stopped the texts, when `dripState` is 'stopped'. */
  textsStopped: { by: 'customer' | 'user' | 'shop'; name: string | null; at: string | null } | null
  /** The last thing they said when a call got through, dated. */
  lastSaid: { reason: 'thinking' | 'needs_changes' | 'callback' | 'sent_estimate'; at: string } | null
  appointmentId: number | null
  appointmentAt: string | null
  appointmentKind: AppointmentKind | null
}

/** Per-shop numbers behind the formula (Settings → Marketing Supercharge). */
export interface FollowUpCadence {
  callEveryDays: number
  /** Human calls in total; call #maxCalls is the last call. */
  maxCalls: number
}

export interface TaskListResponse {
  rows: TaskRow[]
  counts: Record<TaskGroup, number>
  cadence: FollowUpCadence
  generatedAt: string
}

export interface AnswerTaskResponse {
  task: TaskDoc
  next: TaskDoc | null
  estimate: ApiEstimate
}

export const tasksApi = {
  // GET /tasks/team/:teamId — the day's list with group counts.
  list: (teamId: number | string, signal?: AbortSignal) =>
    api.get<TaskListResponse>(`/tasks/team/${teamId}`, undefined, signal),

  // GET /tasks/estimate/:estimateId — one estimate's history, for the timeline.
  byEstimate: (estimateId: number | string) =>
    api.get<{ tasks: TaskDoc[] }>(`/tasks/estimate/${estimateId}`),

  // POST /tasks/:id/answer — what happened. Comes back with the next task so
  // the screen can say "Next: call Wednesday" in the same breath.
  answer: (taskId: number, answer: TaskAnswer, note?: string | null) =>
    api.post<AnswerTaskResponse>(`/tasks/${taskId}/answer`, { answer, note: note ?? null }),

  // POST /tasks/:id/skip — "not today".
  skip: (taskId: number) => api.post<{ task: TaskDoc }>(`/tasks/${taskId}/skip`, {}),

  // POST /tasks/estimate/:id/call — a call in N business days for an estimate
  // with nothing open ("came for the inspection, hasn't booked the drop-off").
  followUpCall: (estimateId: number | string, inDays: number) =>
    api.post<{ estimate: ApiEstimate }>(`/tasks/estimate/${estimateId}/call`, { inDays }),

  // PATCH /estimates/:id/follow-ups/decline — the customer said no somewhere
  // that isn't a task she can answer (a reply). Same record as a task decline.
  decline: (estimateId: number | string, reason: DeclineReason, note?: string | null) =>
    api.patch<{ estimate: ApiEstimate }>(`/estimates/${estimateId}/follow-ups/decline`, {
      reason,
      note: note ?? null,
    }),

  // PATCH /estimates/:id/follow-ups/outreach — set BEFORE the move to
  // InProgress by "Just move it, I'll call them myself".
  setOutreach: (estimateId: number | string, optOut: boolean) =>
    api.patch<{ estimate: ApiEstimate }>(`/estimates/${estimateId}/follow-ups/outreach`, {
      optOut,
    }),

  // PATCH /estimates/:id/follow-ups/snooze — "not now, ask me Tuesday".
  snooze: (estimateId: number | string, action: 'snooze' | 'wake', until?: string | null, note?: string) =>
    api.patch<{ estimate: ApiEstimate }>(`/estimates/${estimateId}/follow-ups/snooze`, {
      action,
      ...(until ? { until } : {}),
      ...(note ? { note } : {}),
    }),
}

// ── Labels shared by the task screen and the estimate timeline ──

export const OUTCOME_LABEL: Record<FollowUpOutcome, string> = {
  reached: 'reached them',
  no_answer: 'no answer',
  voicemail: 'left a voicemail',
  bad_number: 'wrong number',
}

export const DECLINE_LABEL: Record<DeclineReason, string> = {
  price: 'Price',
  another_shop: 'Going to another shop',
  insurance_total_loss: 'Insurance / total loss',
  not_ready: 'Not ready yet',
  no_longer_needed: 'No longer needed',
  unreachable: 'Never reached them',
  other: 'Other',
}

/** "1st try" / "2nd try" — ordinals read faster than "attempt 2". */
export function attemptLabel(n: number): string {
  if (n <= 0) return 'not called yet'
  const suffix = n % 100 >= 11 && n % 100 <= 13 ? 'th' : (['th', 'st', 'nd', 'rd'][n % 10] ?? 'th')
  return `${n}${suffix} try`
}
